import React from "react";

const highlights = [
  { value: "15+", label: "Years in Industry", icon: "🏆" },
  { value: "50+", label: "Countries Served", icon: "🌍" },
  { value: "1,200+", label: "Active Clients", icon: "🤝" },
  { value: "2M+", label: "Tonnes Shipped", icon: "📦" },
];

export default function About() {
  return (
    <section id="about" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image + floating cards */}
          <div className="relative animate-on-scroll">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl shadow-blue-900/20">
              <img
                src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=900&q=80"
                alt="Logistics warehouse operations"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/30 to-transparent" />
            </div>

            {/* Floating card 1 */}
            <div className="absolute -bottom-6 -right-4 bg-white rounded-2xl shadow-xl shadow-blue-900/15 p-4 flex items-center gap-3 border border-slate-100">
              <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center text-green-600 text-xl">
                ✓
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900">ISO Certified</div>
                <div className="text-xs text-slate-500">ISO 9001:2015</div>
              </div>
            </div>

            {/* Floating card 2 */}
            <div className="absolute -top-4 -left-4 bg-blue-700 text-white rounded-2xl shadow-xl p-4">
              <div className="text-2xl font-black">98%</div>
              <div className="text-xs text-blue-200 font-medium">On-Time Rate</div>
            </div>

            {/* Decorative dots grid */}
            <div
              className="absolute -bottom-8 -left-8 w-32 h-32 -z-10 opacity-30"
              style={{
                backgroundImage: "radial-gradient(circle, #1d4ed8 1px, transparent 1px)",
                backgroundSize: "12px 12px",
              }}
            />
          </div>

          {/* Right: Content */}
          <div className="animate-on-scroll">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-widest">About Load2Serve</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 mb-6 leading-tight">
              Built on Trust,<br />
              Driven by <span className="gradient-text">Precision</span>
            </h2>
            <p className="text-slate-500 leading-relaxed mb-4">
              For over 15 years, Load2Serve has been the backbone of global supply chains for businesses ranging from emerging startups to Fortune 500 enterprises. We combine deep logistics expertise with cutting-edge technology to deliver freight solutions that are reliable, transparent, and scalable.
            </p>
            <p className="text-slate-500 leading-relaxed mb-8">
              Our global network spans 50+ countries, with partnerships at major ports and airports worldwide. We're not just a logistics company — we're a strategic partner committed to helping your business grow without borders.
            </p>

            {/* Key points */}
            <ul className="space-y-3 mb-10">
              {[
                "Fully licensed & bonded customs brokers",
                "Real-time shipment tracking via our platform",
                "Dedicated account managers for every client",
                "24/7 emergency logistics support",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-slate-600">{point}</span>
                </li>
              ))}
            </ul>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {highlights.map((h) => (
                <div key={h.label} className="text-center bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <div className="text-xl mb-1">{h.icon}</div>
                  <div className="text-xl font-black text-blue-700">{h.value}</div>
                  <div className="text-xs text-slate-500 font-medium mt-0.5">{h.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
